"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getErrorMessage = exports.isFinleyErrorType = exports.isErrorType = exports.finleyAssertUnreachable = exports.finleyAssert = exports.VendorError = exports.TypeError = exports.Unauthorized = exports.NotFound = exports.Forbidden = exports.CreateError = exports.Conflicts = exports.BadRequest = exports.StructuredPublicError = exports.FinleyError = exports.LogLevel = void 0;
// eslint-disable-next-line max-classes-per-file
const assert_1 = __importDefault(require("assert"));
const finley_types_1 = require("@finley-technologies/finley-types");
exports.LogLevel = finley_types_1.Log.LogLevel;
/**
 * Finely generic error
 */
class FinleyError extends Error {
    constructor(message, options) {
        super(message);
        this.metadata = options === null || options === void 0 ? void 0 : options.metadata;
        this.externalMessage = (options === null || options === void 0 ? void 0 : options.externalMessage) || message;
        this.logLevel = options === null || options === void 0 ? void 0 : options.logLevel;
    }
}
exports.FinleyError = FinleyError;
/** An error that can be directly displayed to customers. */
class StructuredPublicError extends FinleyError {
    constructor(message, options) {
        const { structuredErrors } = options, rest = __rest(options, ["structuredErrors"]);
        const superOptions = Object.assign(Object.assign({}, rest), { externalMessage: `${structuredErrors.length} ${structuredErrors.length === 1 ? 'error' : 'errors'} occurred.` });
        super(message, superOptions);
        this.metadata = Object.assign(Object.assign({}, super.metadata), { structuredErrors });
    }
}
exports.StructuredPublicError = StructuredPublicError;
/**
 * BadRequest
 * This error is primarily used in gateway services, where request is validated from external users.
 * Internal service communication validation should throw FinleyError
 */
class BadRequest extends FinleyError {
    constructor({ message, externalMessage, metadata, logLevel, }) {
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.BadRequest = BadRequest;
/**
 * Conflicts
 * This error should be used for exceptions along the line of:
 *  - state transition error due to invalid signal for current state
 *  - mutation action on already immutable records
 */
class Conflicts extends FinleyError {
    constructor({ message, externalMessage, metadata, logLevel, }) {
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.Conflicts = Conflicts;
/**
 * NOTE:
 * We should be able to intercept DB error, and extend
 * its message
 */
class CreateError extends FinleyError {
    constructor({ resourceType, externalMessage, metadata, logLevel, }) {
        const message = `Unable to create resource type [${resourceType}]`;
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.CreateError = CreateError;
/**
 * Placeholder for unauthorized access (aka, Forbidden)
 * Lookup parameter are no longer required, as some error may not require them.
 * We've also added an optional `action` param, as we may want to know which action was possibly denied.
 */
class Forbidden extends FinleyError {
    constructor({ resourceType, actionType, lookupParam, lookupValue, metadata, logLevel, }) {
        let message = `Not authorized to take action [${actionType}] for resource [${resourceType}].`;
        if (lookupParam || lookupValue) {
            message += ` By param [${lookupParam}] [${lookupValue}]`;
        }
        // const message = `Unable to access resource type [${resourceType}] by param [${lookupParam}] [${lookupValue}]`;
        super(message, { metadata, logLevel });
    }
}
exports.Forbidden = Forbidden;
/**
 * Thrown when specified resource is not found
 */
class NotFound extends FinleyError {
    constructor({ resourceType, lookupParam, lookupValue, metadata, logLevel, }) {
        const message = `Unable to find resource type [${resourceType}] by param [${lookupParam}] [${lookupValue}]`;
        const externalMessage = `Unable to find the requested ${resourceType}`;
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.NotFound = NotFound;
/**
 * Placeholder for authentication error, similar terminology to HTTP code
 * constructor params still needs some designing
 */
class Unauthorized extends FinleyError {
    constructor({ message, metadata, logLevel, }) {
        super(message, { metadata, logLevel });
    }
}
exports.Unauthorized = Unauthorized;
/**
 * Placeholder for typeguard error, similar terminology to HTTP code
 * constructor params still needs some designing
 */
class TypeError extends FinleyError {
    constructor({ resourceType, typeguardName, metadata, logLevel, }) {
        const message = `Resource type [${resourceType}] is invalid type, failing typeguard check [${typeguardName}]`;
        const externalMessage = `Unable to parse the ${resourceType} type`;
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.TypeError = TypeError;
/**
 * Placeholder for 3rd party vendor error, similar terminology to HTTP code
 * constructor params still needs some designing
 */
class VendorError extends FinleyError {
    constructor({ vendorName, metadata, errStack, logLevel, }) {
        const message = `Unexpected error with vendor ${vendorName}`;
        super(message, { metadata, errStack, logLevel });
    }
}
exports.VendorError = VendorError;
// NOTE: assertion method will be exposed directly, to make TS compiler happy
const finleyAssert = (condition, err) => {
    if (condition) {
        return;
    }
    const assertError = err instanceof Error ? err : new FinleyError(err);
    throw assertError;
};
exports.finleyAssert = finleyAssert;
// this compile time assert ensures that a function is exhaustive
// useful to use in your default case for switch statement.
const finleyAssertUnreachable = (x, message) => {
    (0, exports.finleyAssert)(false, message !== null && message !== void 0 ? message : `reached Unreachable ${x}`);
};
exports.finleyAssertUnreachable = finleyAssertUnreachable;
/**
 * see if this is a valid error for our error handling
 * @param err potential error
 * @returns boolean true if typeof error
 */
const isErrorType = (err) => {
    return err instanceof Error || err instanceof assert_1.default.AssertionError;
};
exports.isErrorType = isErrorType;
/**
 * see if this is a valid finley error for our error handling
 * @param err potential error
 * @returns boolean true if typeof error
 */
const isFinleyErrorType = (err) => {
    return err instanceof FinleyError;
};
exports.isFinleyErrorType = isFinleyErrorType;
const getErrorMessage = (err, defaultMessage) => {
    const message = (0, exports.isFinleyErrorType)(err) || (0, exports.isErrorType)(err)
        ? err.message
        : defaultMessage !== null && defaultMessage !== void 0 ? defaultMessage : 'Unknown error occurred.';
    return message;
};
exports.getErrorMessage = getErrorMessage;
//# sourceMappingURL=error.js.map